import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Space, Tag, message, Spin, Alert, Grid } from 'antd';
import { ArrowLeftOutlined, DisconnectOutlined } from '@ant-design/icons';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { useAuth } from '../contexts/AuthContext';

type ConnectionStatus = 'connecting' | 'waiting' | 'connected' | 'disconnected' | 'error';

const { useBreakpoint } = Grid;

const ShellConsole: React.FC = () => {
  const { deviceCode } = useParams<{ deviceCode: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const commandBuffer = useRef<string>('');
  const statusRef = useRef<ConnectionStatus>('connecting');
  
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // 同步更新 statusRef
  const updateStatus = (newStatus: ConnectionStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
  };

  useEffect(() => {
    // 权限检查
    if (user?.userType !== 'super_admin' && user?.userType !== 'admin') {
      message.error('无权限访问此功能');
      navigate('/devices');
      return;
    }

    if (!deviceCode || !token) {
      message.error('缺少必要参数');
      navigate('/devices');
      return;
    }

    // 初始化 xterm
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        selectionBackground: '#264f78',
      },
      rows: 24,
      cols: 80,
    });
    
    const fit = new FitAddon();
    term.loadAddon(fit);
    
    if (terminalRef.current) {
      term.open(terminalRef.current);
      fit.fit();
    }
    
    terminalInstance.current = term;
    fitAddon.current = fit;
    
    term.writeln('\x1b[33m正在连接设备...\x1b[0m');
    
    // 建立 WebSocket 连接
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/admin?token=${encodeURIComponent(token)}&deviceCode=${encodeURIComponent(deviceCode)}`;
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('[Shell] WebSocket connected');
      updateStatus('waiting');
    };
    
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        
        switch (msg.type) {
          case 'waiting':
            term.writeln('\x1b[33m' + msg.message + '\x1b[0m');
            break;
          
          case 'timeout_warning':
            // 30秒超时警告，但继续等待
            term.writeln('\x1b[33m' + (msg.message || '设备暂未响应，继续等待中...') + '\x1b[0m');
            break;
            
          case 'device_connected':
            updateStatus('connected');
            term.writeln('\x1b[32m设备已连接，可以开始输入命令\x1b[0m');
            term.writeln('');
            term.write('$ ');
            break;
            
          case 'output':
            // 设备返回的命令输出
            term.write(msg.data || '');
            break;
            
          case 'exit':
            // 命令执行完成
            term.writeln('');
            term.write('$ ');
            break;
            
          case 'error':
            term.writeln('\x1b[31m错误: ' + (msg.message || '未知错误') + '\x1b[0m');
            if (statusRef.current !== 'connected') {
              updateStatus('error');
              setErrorMessage(msg.message || '连接失败');
            }
            break;
            
          case 'device_disconnected':
            updateStatus('disconnected');
            term.writeln('');
            term.writeln('\x1b[31m设备已断开连接\x1b[0m');
            break;
            
          default:
            console.log('[Shell] Unknown message type:', msg.type);
        }
      } catch (e) {
        console.error('[Shell] Failed to parse message:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[Shell] WebSocket error:', error);
      updateStatus('error');
      setErrorMessage('WebSocket 连接错误');
      term.writeln('\x1b[31m连接错误\x1b[0m');
    };
    
    ws.onclose = (event) => {
      console.log('[Shell] WebSocket closed:', event.code, event.reason);
      if (statusRef.current !== 'disconnected' && statusRef.current !== 'error') {
        updateStatus('disconnected');
        term.writeln('');
        term.writeln('\x1b[31m连接已关闭\x1b[0m');
      }
    };
    
    // 处理终端输入
    term.onData((data) => {
      if (statusRef.current !== 'connected' || ws.readyState !== WebSocket.OPEN) {
        return;
      }
      
      // 处理特殊键
      if (data === '\r') {
        // Enter 键 - 发送命令
        term.writeln('');
        if (commandBuffer.current.trim()) {
          ws.send(JSON.stringify({
            type: 'command',
            command: commandBuffer.current
          }));
        } else {
          term.write('$ ');
        }
        commandBuffer.current = '';
      } else if (data === '\x7f') {
        // Backspace
        if (commandBuffer.current.length > 0) {
          commandBuffer.current = commandBuffer.current.slice(0, -1);
          term.write('\b \b');
        }
      } else if (data === '\x03') {
        // Ctrl+C
        ws.send(JSON.stringify({ type: 'interrupt' }));
        term.writeln('^C');
        term.write('$ ');
        commandBuffer.current = '';
      } else if (data >= ' ' || data === '\t') {
        // 可打印字符
        commandBuffer.current += data;
        term.write(data);
      }
    });
    
    // 窗口大小变化时自适应
    const handleResize = () => {
      if (fitAddon.current) {
        fitAddon.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);
    
    // 清理
    return () => {
      window.removeEventListener('resize', handleResize);
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      term.dispose();
    };
  }, [deviceCode, token, user, navigate]);

  const handleDisconnect = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    updateStatus('disconnected');
  };

  const handleBack = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    navigate('/devices');
  };

  const getStatusTag = () => {
    switch (status) {
      case 'connecting':
        return <Tag color="processing">连接中...</Tag>;
      case 'waiting':
        return <Tag color="warning">等待设备响应...</Tag>;
      case 'connected':
        return <Tag color="success">已连接</Tag>;
      case 'disconnected':
        return <Tag color="default">已断开</Tag>;
      case 'error':
        return <Tag color="error">连接失败</Tag>;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            <span style={{ fontSize: isMobile ? 14 : 16 }}>📱 {isMobile ? '' : 'Shell 控制台 - '}设备 {deviceCode}</span>
            {getStatusTag()}
          </Space>
        }
        extra={
          <Space size={isMobile ? 'small' : 'middle'} wrap>
            {status === 'connected' && (
              <Button
                icon={<DisconnectOutlined />}
                onClick={handleDisconnect}
                danger
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? '断开' : '断开连接'}
              </Button>
            )}
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '返回' : '返回设备列表'}
            </Button>
          </Space>
        }
        styles={{ body: { padding: isMobile ? 8 : 24 } }}
      >
        {status === 'error' && (
          <Alert
            message="连接失败"
            description={errorMessage}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {(status === 'connecting' || status === 'waiting') && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin tip={status === 'connecting' ? '正在连接服务器...' : '等待设备响应...'} />
          </div>
        )}
        
        <div
          ref={terminalRef}
          style={{
            height: isMobile ? '350px' : '500px',
            backgroundColor: '#1e1e1e',
            borderRadius: '4px',
            padding: '8px',
          }}
        />
        
        {!isMobile && (
        <div style={{ marginTop: 16, color: '#666', fontSize: 12 }}>
          <p>提示：</p>
          <ul style={{ marginLeft: 20 }}>
            <li>输入命令后按 Enter 执行</li>
            <li>按 Ctrl+C 可中断当前命令</li>
            <li>命令在设备上以 system 权限执行</li>
          </ul>
          <p style={{ marginTop: 8 }}>特殊命令：</p>
          <ul style={{ marginLeft: 20 }}>
            <li><code>yiguan list-launcher-apps</code> - 列出所有有桌面图标的应用及安装信息</li>
            <li><code>yiguan help</code> - 显示所有可用的特殊命令</li>
          </ul>
        </div>
        )}
      </Card>
    </div>
  );
};

export default ShellConsole;
